import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Music as MusicIcon,
  BookOpen,
  ShoppingBag,
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KINDS = [
  { key: "music", label: "Music", icon: MusicIcon, blank: { title: "", theme: "", description: "", href: "", provider: "Spotify", accent_color: "#2AADA0", cover_url: "", is_hidden: false } },
  { key: "books", label: "Books", icon: BookOpen, blank: { title: "", author: "", topic: "", description: "", href: "", cover_url: "", is_hidden: false } },
  { key: "products", label: "Shopping", icon: ShoppingBag, blank: { name: "", category: "safety", price: "", description: "", href: "", icon_name: "ShoppingBag", tint_from: "#2AADA0", tint_to: "#238C82", is_hidden: false } },
];

const PRODUCT_CATEGORIES = [
  { id: "safety", label: "Home Safety" },
  { id: "mobility", label: "Mobility & Fall Prevention" },
  { id: "bathroom", label: "Bathroom & Toileting" },
  { id: "comfort", label: "Comfort & Sleep" },
  { id: "cognition", label: "Cognition & Engagement" },
  { id: "caregiver", label: "For the Caregiver" },
];

// Lucide icon names admins can pick from (matches ShoppingPage renderer)
const ICON_CHOICES = [
  "ShoppingBag", "ShieldCheck", "Bath", "Toilet", "Bed", "BedDouble", "Watch",
  "Pill", "Puzzle", "Clock", "NotebookPen", "Move", "Heart", "Home", "Utensils",
];

export default function ExtrasAdmin() {
  const [activeKind, setActiveKind] = useState("music");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode: 'new'|'edit', data: {...} }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (kind) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/extras/${kind}`);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load extras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(activeKind); }, [activeKind, load]);

  const kindMeta = KINDS.find((k) => k.key === activeKind);

  const openNew = () => setEditing({ mode: "new", data: { ...kindMeta.blank } });
  const openEdit = (item) => setEditing({ mode: "edit", data: { ...item } });
  const closeEdit = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "new") {
        await axios.post(`${API}/admin/extras/${activeKind}`, editing.data);
        toast.success("Added");
      } else {
        await axios.put(`${API}/admin/extras/${activeKind}/${editing.data.id}`, editing.data);
        toast.success("Updated");
      }
      await load(activeKind);
      closeEdit();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || "Please try again" });
    } finally {
      setSaving(false);
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.title || item.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/extras/${activeKind}/${item.id}`);
      toast.success("Deleted");
      await load(activeKind);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const reorder = async (item, direction) => {
    try {
      await axios.put(`${API}/admin/extras/${activeKind}/${item.id}/reorder`, { direction });
      await load(activeKind);
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  const toggleVisibility = async (item) => {
    try {
      await axios.put(`${API}/admin/extras/${activeKind}/${item.id}/visibility`, { is_hidden: !item.is_hidden });
      await load(activeKind);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <section data-testid="admin-extras-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-accent">Extras — Music, Books &amp; Shopping</h2>
          <p className="text-sm text-text-secondary font-body mt-1">
            Curate what appears on <code>/music</code>, <code>/books</code>, and <code>/shopping</code>. Changes go live instantly.
          </p>
        </div>
      </div>

      {/* Kind tabs */}
      <div className="inline-flex bg-mist rounded-full p-1 border border-[#E2E8F0] mb-6">
        {KINDS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`extras-tab-${key}`}
            onClick={() => setActiveKind(key)}
            className={`px-4 py-2 rounded-full font-body font-semibold text-sm transition-all flex items-center gap-2 ${
              activeKind === key
                ? "bg-primary text-white shadow-md"
                : "text-text-secondary hover:text-primary"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-body text-text-secondary">
          {loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"}`}
        </p>
        <Button
          data-testid="extras-add-btn"
          onClick={openNew}
          className="bg-secondary text-white hover:bg-[#C96540] rounded-full font-body font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> Add {kindMeta.label.slice(0, -1) || kindMeta.label}
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-text-secondary font-body text-sm">
          Nothing here yet. Click <strong>Add</strong> above to create your first entry.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div
              key={item.id}
              data-testid={`extras-item-${item.id}`}
              className={`flex flex-wrap items-center gap-3 border border-[#E2E8F0] rounded-xl p-3 bg-white ${item.is_hidden ? "opacity-60" : ""}`}
            >
              {/* Reorder */}
              <div className="flex flex-col">
                <button
                  data-testid={`extras-up-${item.id}`}
                  onClick={() => reorder(item, "up")}
                  disabled={idx === 0}
                  className="p-0.5 text-text-secondary hover:text-primary disabled:opacity-30"
                  aria-label="Move up"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  data-testid={`extras-down-${item.id}`}
                  onClick={() => reorder(item, "down")}
                  disabled={idx === items.length - 1}
                  className="p-0.5 text-text-secondary hover:text-primary disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {/* Thumb / accent */}
              {activeKind !== "products" && item.cover_url && (
                <img src={item.cover_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-[#E2E8F0]" onError={(e) => { e.target.style.display = "none"; }} />
              )}
              {activeKind === "products" && (
                <div
                  className="w-12 h-12 rounded-lg flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${item.tint_from || "#2AADA0"}, ${item.tint_to || "#238C82"})` }}
                />
              )}

              <div className="flex-1 min-w-0">
                <p className="font-body font-bold text-text-primary truncate">
                  {item.title || item.name}
                  {item.is_hidden && <span className="ml-2 text-xs uppercase font-bold tracking-wider text-gray-500">Hidden</span>}
                </p>
                <p className="text-xs text-text-secondary font-body truncate">
                  {activeKind === "music" && `${item.provider || ""} · ${item.theme || ""}`}
                  {activeKind === "books" && `${item.author || ""} · ${item.topic || ""}`}
                  {activeKind === "products" && `${PRODUCT_CATEGORIES.find(c => c.id === item.category)?.label || item.category} · ${item.price || ""}`}
                </p>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  data-testid={`extras-visibility-${item.id}`}
                  onClick={() => toggleVisibility(item)}
                  variant="ghost"
                  size="sm"
                  className="text-text-secondary hover:text-primary"
                  title={item.is_hidden ? "Show on public page" : "Hide from public page"}
                >
                  {item.is_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  data-testid={`extras-edit-${item.id}`}
                  onClick={() => openEdit(item)}
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:bg-primary/5"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`extras-delete-${item.id}`}
                  onClick={() => del(item)}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Add dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-accent">
              {editing?.mode === "new" ? `Add new ${kindMeta.label.toLowerCase().replace(/s$/, "")}` : `Edit ${editing?.data?.title || editing?.data?.name || "item"}`}
            </DialogTitle>
            <DialogDescription className="font-body">
              Changes go live the moment you save. To temporarily unpublish, use the hide toggle instead of deleting.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4 py-2">
              {/* Fields per kind */}
              {activeKind === "music" && (
                <>
                  <FormField label="Title" testId="ex-field-title">
                    <Input value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} placeholder="Memory Lane — 40s & 50s Standards" data-testid="ex-input-title" />
                  </FormField>
                  <FormField label="Theme / tagline" testId="ex-field-theme">
                    <Input value={editing.data.theme} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, theme: e.target.value } })} placeholder="Memory care · Reminiscence" data-testid="ex-input-theme" />
                  </FormField>
                  <FormField label="Description" testId="ex-field-desc">
                    <Textarea value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} rows={3} data-testid="ex-input-desc" />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Streaming URL" testId="ex-field-href">
                      <Input value={editing.data.href} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, href: e.target.value } })} placeholder="https://open.spotify.com/..." data-testid="ex-input-href" />
                    </FormField>
                    <FormField label="Provider label" testId="ex-field-provider">
                      <Input value={editing.data.provider} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, provider: e.target.value } })} placeholder="Spotify / YouTube / Featured Artist" data-testid="ex-input-provider" />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Cover image URL" testId="ex-field-cover">
                      <Input value={editing.data.cover_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, cover_url: e.target.value } })} placeholder="https://images.unsplash.com/..." data-testid="ex-input-cover" />
                    </FormField>
                    <FormField label="Accent color (hex)" testId="ex-field-accent">
                      <div className="flex gap-2 items-center">
                        <Input type="color" value={editing.data.accent_color} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, accent_color: e.target.value } })} className="w-16 h-10 cursor-pointer" />
                        <Input value={editing.data.accent_color} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, accent_color: e.target.value } })} placeholder="#2AADA0" />
                      </div>
                    </FormField>
                  </div>
                </>
              )}

              {activeKind === "books" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Title" testId="ex-field-title">
                      <Input value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} placeholder="The 36-Hour Day" data-testid="ex-input-title" />
                    </FormField>
                    <FormField label="Author(s)" testId="ex-field-author">
                      <Input value={editing.data.author} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, author: e.target.value } })} placeholder="Nancy L. Mace & Peter V. Rabins" data-testid="ex-input-author" />
                    </FormField>
                  </div>
                  <FormField label="Topic tag" testId="ex-field-topic">
                    <Input value={editing.data.topic} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, topic: e.target.value } })} placeholder="Dementia · Caregiver Handbook" data-testid="ex-input-topic" />
                  </FormField>
                  <FormField label="Description" testId="ex-field-desc">
                    <Textarea value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} rows={4} data-testid="ex-input-desc" />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Buy link (Amazon w/ your affiliate tag)" testId="ex-field-href">
                      <Input value={editing.data.href} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, href: e.target.value } })} placeholder="https://www.amazon.com/...?tag=cherylgullo-20" data-testid="ex-input-href" />
                    </FormField>
                    <FormField label="Cover image URL" testId="ex-field-cover">
                      <Input value={editing.data.cover_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, cover_url: e.target.value } })} placeholder="https://covers.openlibrary.org/..." data-testid="ex-input-cover" />
                    </FormField>
                  </div>
                </>
              )}

              {activeKind === "products" && (
                <>
                  <FormField label="Product name" testId="ex-field-name">
                    <Input value={editing.data.name} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} placeholder="Shower Chair with Back & Armrests" data-testid="ex-input-name" />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Category" testId="ex-field-category">
                      <select
                        value={editing.data.category}
                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, category: e.target.value } })}
                        className="w-full h-10 rounded-md border border-input bg-white px-3 py-2 text-sm font-body"
                        data-testid="ex-input-category"
                      >
                        {PRODUCT_CATEGORIES.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Price (display string)" testId="ex-field-price">
                      <Input value={editing.data.price} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, price: e.target.value } })} placeholder="≈ $60" data-testid="ex-input-price" />
                    </FormField>
                  </div>
                  <FormField label="Description" testId="ex-field-desc">
                    <Textarea value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} rows={3} data-testid="ex-input-desc" />
                  </FormField>
                  <FormField label="Amazon / manufacturer link" testId="ex-field-href">
                    <Input value={editing.data.href} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, href: e.target.value } })} placeholder="https://www.amazon.com/s?k=...&tag=cherylgullo-20" data-testid="ex-input-href" />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField label="Icon" testId="ex-field-icon">
                      <select
                        value={editing.data.icon_name}
                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, icon_name: e.target.value } })}
                        className="w-full h-10 rounded-md border border-input bg-white px-3 py-2 text-sm font-body"
                        data-testid="ex-input-icon"
                      >
                        {ICON_CHOICES.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Gradient — from" testId="ex-field-tint-from">
                      <div className="flex gap-2 items-center">
                        <Input type="color" value={editing.data.tint_from} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tint_from: e.target.value } })} className="w-14 h-10 cursor-pointer" />
                        <Input value={editing.data.tint_from} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tint_from: e.target.value } })} />
                      </div>
                    </FormField>
                    <FormField label="Gradient — to" testId="ex-field-tint-to">
                      <div className="flex gap-2 items-center">
                        <Input type="color" value={editing.data.tint_to} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tint_to: e.target.value } })} className="w-14 h-10 cursor-pointer" />
                        <Input value={editing.data.tint_to} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tint_to: e.target.value } })} />
                      </div>
                    </FormField>
                  </div>
                </>
              )}

              {/* Hidden toggle */}
              <div className="flex items-center justify-between pt-3 border-t border-[#E2E8F0]">
                <div>
                  <Label className="font-body font-semibold">Hide from public page</Label>
                  <p className="text-xs text-text-secondary font-body mt-0.5">Kept in your library but not shown on <code>/{activeKind === "products" ? "shopping" : activeKind}</code>.</p>
                </div>
                <Switch
                  checked={editing.data.is_hidden}
                  onCheckedChange={(v) => setEditing({ ...editing, data: { ...editing.data, is_hidden: v } })}
                  data-testid="ex-input-hidden"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeEdit} disabled={saving} data-testid="ex-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-white hover:bg-primary-dark rounded-full font-bold" data-testid="ex-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FormField({ label, children, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label className="font-body font-semibold text-sm">{label}</Label>
      {children}
    </div>
  );
}
